/**
 * Feather tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const featherTattoosPack: ToilePack = {
    slug: 'feather-tattoos',
    category: 'tattoo',
    name: 'Feather tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'birds', label: 'Feather and birds', subject: 'a single long feather whose upper tip breaks apart into a small flock of birds flying away'},
        {key: 'peacock', label: 'Peacock feather', subject: 'a peacock feather, the mor pankh, its eye drawn in rings at the top and fine barbs down the shaft'},
        {key: 'hanging', label: 'Hanging feathers', subject: 'two feathers hanging from a short string of round beads, one longer than the other'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['an arrow', 'gold ink', 'ribs'],
    directionHint: 'try: an arrow',
    seo: {
        title: 'Feather tattoo designs: feather to birds, peacock, hanging',
        metaDescription: 'Nine feather tattoo designs: a feather breaking into birds, a peacock feather and hanging feathers on beads, in three styles.',
        h1: 'Feather tattoo designs: feather into birds, peacock feather, hanging feathers',
        tagline: 'Three feathers, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Feather tattoo designs, light and long, fitting the forearm, the ribs or the place behind the ear. The nine sheets show a feather whose tip breaks into a flock of birds, a peacock feather, the mor pankh that Krishna wears in his crown, and two feathers hanging from beads. Each is drawn as traditional flash, fine line and dotwork. Type a different bird or twist, such as a gold arrow, and the same styles can be asked for.',
        useCases: [
            'a long feather along the forearm',
            'a rib piece with a peacock feather',
            'a small feather behind the ear',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine feather designs: a feather into birds, a peacock feather and hanging feathers, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'What is the mor pankh?', a: 'It is the peacock feather worn by Krishna in his crown, drawn here as that feather with its eye in rings.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
